import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { useUsersContext } from "../../../context/usersContext";

// "Proven" tasks are the curated platform-wide library — the backend marks
// them type "super-admin" (same split the chat-home Task Library uses).
const PROVEN_TYPE = "super-admin";

const formatRepeat = (repeat) =>
  repeat
    ? repeat.charAt(0).toUpperCase() + String(repeat).slice(1).toLowerCase()
    : null;

/**
 * The "Or pick a proven Task" grid under the Automate composer (Lovable
 * /create-task). Clicking Automate re-enters the create route with the
 * task's prompt as `autoloadfirsttext`, which the wizard's open effect
 * loads into the composer.
 */
const ProvenTaskGrid = () => {
  const navigate = useNavigate();
  const { tasksData } = useUsersContext();
  const [filter, setFilter] = useState("");

  const proven = useMemo(
    () =>
      (tasksData?.prompt || []).filter(
        (t) => String(t?.type) === PROVEN_TYPE,
      ),
    [tasksData],
  );

  const shown = useMemo(() => {
    const term = filter.trim().toLowerCase();
    if (!term) return proven;
    return proven.filter(
      (t) =>
        (t?.title || "").toLowerCase().includes(term) ||
        (t?.prompt_value || "").toLowerCase().includes(term) ||
        (t?.output || "").toLowerCase().includes(term),
    );
  }, [proven, filter]);

  if (!proven.length) return null;

  const automate = (task) => {
    const prompt = task?.prompt_value?.trim();
    if (!prompt) return;
    navigate("/app/chat/tasks/new", {
      state: { autoloadfirsttext: prompt },
      replace: true,
    });
    try {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      /* jsdom */
    }
  };

  return (
    <section className="mt-10 mb-16">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
            Or pick a proven Task
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {shown.length} of {proven.length} shown
          </p>
        </div>
        <div className="relative sm:w-80">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter by tool, category, sector..."
            aria-label="Filter proven Tasks"
            className="h-9 w-full rounded-md border border-input bg-background !pl-8 pr-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {shown.map((task) => (
          <div
            key={task._id || task.id || task.title}
            className="interactive-surface flex flex-col gap-3 rounded-xl border border-border bg-card p-5"
          >
            <div>
              <h3 className="font-mono text-sm font-semibold text-foreground">
                {task.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground line-clamp-2">
                {task.prompt_value}
              </p>
            </div>
            <div className="mt-auto flex items-end justify-between gap-3">
              <button
                type="button"
                onClick={() => automate(task)}
                className="interactive-chip h-8 rounded-md border border-border bg-background px-3 text-xs font-semibold text-foreground transition-colors hover:bg-muted/60"
              >
                Automate
              </button>
              {task.repeat && String(task.repeat).toLowerCase() !== "none" && (
                <div className="text-right">
                  <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                    Suggested
                  </p>
                  <p className="text-xs font-semibold text-foreground">
                    Schedule {formatRepeat(task.repeat)}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
        {shown.length === 0 && (
          <p className="col-span-full text-sm text-muted-foreground">
            No proven Tasks match "{filter}".
          </p>
        )}
      </div>
    </section>
  );
};

export default ProvenTaskGrid;
